<template>
  <div class="sheet widget mt-4">
    <div class="widget-content" v-if="importing">
      <AlertBlock :text="$t('training.program.alert.importing')" type="success mb-0" />
    </div>
    <template v-if="hasBeenImportedOnce">
      <div class="widget-heading flex-column">
        <div class="sheet__heading">
          <h2 class="textStyle">
            <FontAwesomeIcon class="me-2" icon="fa-light fa-square-poll-vertical" />
            {{ $t("training.program.general_informations") }}
          </h2>
          <button class="btn-icon" @click="edit('general-info')">
            <FontAwesomeIcon icon="fa-light fa-pen" />
          </button>
        </div>
      </div>
      <div class="widget-content">
        <div class="sheet__row">
          <div class="field">{{ $t("training.program.graduation") }}</div>
          <div class="text">{{ training.diploma }}</div>
        </div>

        <div class="sheet__row">
          <div class="field">{{ $t("training.program.route") }}</div>
          <div class="text">{{ training.pathway }}</div>
        </div>

        <div class="sheet__row" v-if="training?.titleRncp?.code">
          <div class="field">{{ $t("training.program.rncp_code") }}</div>
          <div class="text">{{ training.titleRncp.code }}</div>
        </div>

        <div class="sheet__row" v-if="training?.degree?.degreeLevel?.code">
          <div class="field">{{ $t("training.program.graduation_type") }}</div>
          <div class="text">
            <TrainingRncpLevelComponent :level="training.degree.degreeLevel.code" />
          </div>
        </div>

        <div class="sheet__row">
          <div class="field">{{ $t("training.program.total_volume") }}</div>
          <div class="text">{{ training.totalHours }}h</div>
        </div>

        <div class="sheet__row">
          <div class="field">{{ $t("training.program.ects_credits") }}</div>
          <div class="text">{{ training.ectsCredits }}</div>
        </div>

        <TrainingProgramAccordionComponent :training="training" @edit="edit" />

        <AlertBlock
          :title="$t('training.program.alert.note')"
          :text="$t('training.program.alert.note_text')"
          type="info"
          icon="lightbulb-exclamation"
        />
      </div>
    </template>
    <div class="widget-content" v-else-if="!importing">
      <AlertBlock :text="$t('training.program.alert.empty_general_informations')" type="info mb-0" />
    </div>
    <BlockLoader :loading="loading" />
  </div>
  <ModalFormComponent :models="models" :title="modalTitle" @submit="modalSubmit" />
</template>

<script>
import AlertBlock from "@/components/blocks/AlertBlock.vue";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import { dateFormatter } from "@/services/utilities/date-formatter";
import { mapState } from "pinia";
import { useNotificationStore } from "@/store/notifications/notification";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import TrainingRncpLevelComponent from "@/components/training/rncp/TrainingRncpLevelComponent.vue";
import TrainingProgramAccordionComponent from "@/components/training/program/TrainingProgramAccordionComponent.vue";
import ModalFormComponent from "@/components/form/ModalFormComponent.vue";
import { trainingManager } from "@/services/training/training-manager";
import { formManager } from "@/services/form/form-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";

export default {
  name: "TrainingProgramSheetComponent",
  emits: ["refresh"],
  components: {
    ModalFormComponent,
    TrainingProgramAccordionComponent,
    TrainingRncpLevelComponent,
    FontAwesomeIcon,
    BlockLoader,
    AlertBlock,
  },
  props: {
    hasBeenImportedOnce: {
      type: Boolean,
      required: true,
    },
    importing: {
      type: Boolean,
      required: true,
    },
    training: {
      type: Object,
      required: true,
    },
    active: {
      type: Boolean,
      required: true,
    },
    warnings: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      data: {},
      alertTitle: "",
      isAlert: false,
      models: [],
      modalTitle: null,
      modalType: null,
    };
  },
  computed: {
    ...mapState(useNotificationStore, {
      status: (store) => store.status,
      messages: (store) => store.messages,
    }),
  },
  methods: {
    dateFormatter() {
      return dateFormatter;
    },

    edit(type) {
      this.modalType = type;
      this.modalTitle = this.$t("training.program.modal." + type);
      this.form();
    },

    clearData() {
      this.data = {};
    },

    async form() {
      this.models = [];
      this.loading = true;
      this.models = await trainingManager.form(
        "program/edit/" + this.modalType,
        null,
        false,
        this.$route.params.trainingId,
      );
      this.loading = false;
    },

    async modalSubmit() {
      const params = formManager.processForm(this.models);
      this.loading = true;

      const response = await trainingManager.put(
        null,
        this.$route.params.trainingId,
        "program/edit/" + this.modalType,
        params,
      );

      const error = response.hasError || (response.success !== undefined && !response.success);
      const message = response?.response?.data?.message;

      notificationManager.showNotification(
        !error ? "success" : "error",
        !error
          ? i18n.global.tc("global.edit.success", 2, {
              name: i18n.global.tc("studea_manager.training.title", 1),
            })
          : message
            ? message
            : i18n.global.t("signed_error"),
      );
      this.loading = false;
      this.refresh();
      this.clearModal();
    },

    refresh() {
      this.$emit("refresh");
    },

    clearModal() {
      this.models = [];
      this.modalTitle = "";
      this.modalType = "";
    },
  },
};
</script>

<style lang="scss" scoped>
.sheet {
  &__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    &__label {
      text-transform: uppercase;
      color: var(--primary-color);
      font-weight: 600;
    }
  }

  &__row {
    padding: 8px 0;
    display: flex;
    font-size: 14px;
    align-items: center;
    min-height: 40px;

    .field {
      width: 25%;
      font-weight: 600;
    }

    .text {
      width: 75%;
    }
  }

  &__row + &__row {
    border-top: 1px dashed $m-color_6;
  }

  &__tags {
    color: $m_color_7;
    margin-bottom: 25px;
  }

  &__date {
    &__label {
      color: $m_color_7;
      font-size: 14px;
      font-weight: 500;
      text-transform: uppercase;
    }
  }

  &__accordion {
    &__card + &__card {
      margin-top: 15px;
    }

    &__card {
      .card-header {
        border-bottom: none;
        padding: 20px;
      }
    }

    &__heading {
      display: flex;
      position: relative;
      font-weight: 700;
      color: var(--primary-color);

      .accordion-icon {
        margin-right: 8px;
        font-size: 16px;
      }

      .icons {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }
}

.widget-content,
.widget-heading {
  @include up-to-md {
    padding: 15px 10px !important;
  }
}

.widget-content {
  padding-bottom: 0 !important;
}
</style>
